/**
 * CampaignsPage.jsx — Week 2 Fix
 * - Channel filter pills (All / TikTok / Snapchat / Google / Meta / Other)
 * - Decision filter (All / CONTINUE / PAUSE / STOP)
 * - Text search by campaign name
 * - Sort (newest / most evaluations / last decision)
 * - Grid / list view toggle
 * - Better card info display
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { functions, db } from "../../firebaseConfig";
import { channelDisplay } from "../../lib/channels";
import { httpsCallable } from "firebase/functions";
import { collection, query, where, getDocs, orderBy, doc, deleteDoc } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPlus, FiTarget, FiTrash2,
  FiSearch, FiFilter, FiGrid, FiList, FiCheckCircle,
  FiXCircle, FiPauseCircle, FiChevronDown, FiHelpCircle,
} from "react-icons/fi";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import Navigation from "../../components/layout/Navigation";
import UsageStatsWidget from "../../components/dashboard/UsageStatsWidget";
import { useQuotas } from "../../hooks/useQuotas";
import UpgradeCTA from "../../components/plans/UpgradeCTA";
import { useDarkMode } from "../../hooks/useDarkMode";

/* ── constants ───────────────────────────────────────────── quota */
const CHANNELS = ["all", "tiktok", "snapchat", "google", "meta", "other"];
// Set of canonical channel ids the UI knows how to render with
// dedicated colours and translated labels. Anything outside this set
// (e.g. a user-supplied "LinkedIn") is treated as a custom value and
// displayed verbatim — that's how the "Other → custom name" flow
// round-trips: the typed string IS the channel, not a separate field.
const KNOWN_CHANNELS = new Set(["tiktok", "snapchat", "google", "meta", "other"]);

// Backend (evaluateCampaign in functions/index.js) normalizes PAUSE → ADJUST
// before saving. The dashboard must use the same value or filtering and
// styling will silently miss every ADJUST decision.
const DECISIONS = ["all", "CONTINUE", "ADJUST", "STOP"];

const CHANNEL_COLORS = {
  tiktok:   "border-pink-500    bg-pink-500/10    text-pink-400",
  snapchat: "border-yellow-500  bg-yellow-500/10  text-yellow-400",
  google:   "border-blue-500    bg-blue-500/10    text-blue-400",
  meta:     "border-indigo-500  bg-indigo-500/10  text-indigo-400",
  other:    "border-gray-500    bg-gray-500/10    text-gray-400",
};
const channelColor = (ch) => CHANNEL_COLORS[ch] || CHANNEL_COLORS.other;

/**
 * channelLabel(ch, lang)
 *
 * Single source of truth for displaying a channel value across the
 * filter pills, the list cards, and the detail header. Three buckets:
 *
 *   1. "all"        → translated "All" / "الكل"
 *   2. known id     → branded label ("Google Ads" / Tiktok / etc.)
 *                     with Arabic translation for "Other" → "أخرى"
 *   3. anything else → the raw stored string, rendered verbatim. This
 *      is the user's custom value from the "Other" flow, e.g. they
 *      typed "LinkedIn" in the modal and it's now their channel.
 */
function channelLabel(ch, lang, t) {
  if (!ch || ch === "all") return t("all_label");
  return channelDisplay(ch, lang);
}

const DECISION_STYLES = {
  CONTINUE: { icon: <FiCheckCircle className="text-sm" />, cls: "text-green-400 bg-green-500/10 border-green-500/30" },
  // ADJUST is the canonical name used by the backend; keep PAUSE as a
  // backward-compatible alias so any pre-existing decisions saved as
  // PAUSE before the normalize was deployed still render correctly.
  ADJUST:   { icon: <FiPauseCircle className="text-sm" />, cls: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30" },
  PAUSE:    { icon: <FiPauseCircle className="text-sm" />, cls: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30" },
  STOP:     { icon: <FiXCircle className="text-sm" />,     cls: "text-red-400 bg-red-500/10 border-red-500/30" },
};

// Treat legacy "PAUSE" rows as "ADJUST" — used everywhere we compare a
// stored decision value to a known constant (counters, filters, lookups).
const normalizeDecision = (d) => (d === "PAUSE" ? "ADJUST" : d);

const SORT_OPTIONS = ["newest", "oldest", "most_evals", "last_decision"];

/* ─────────────────────────────────────────────────────────── */
const CampaignsPage = () => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const { t, isRTL, lang } = useLanguage();
  const { canUseCampaigns } = useFeatureFlags();
  const { usage, getWarningStatus } = useQuotas();
  const { darkMode } = useDarkMode();

  const [campaigns,       setCampaigns]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error,           setError]           = useState("");

  // Filters
  const [search,          setSearch]          = useState("");
  const [channelFilter,   setChannelFilter]   = useState("all");
  const [decisionFilter,  setDecisionFilter]  = useState("all");
  const [sortBy,          setSortBy]          = useState("newest");
  const [viewMode,        setViewMode]        = useState("grid"); // "grid" | "list"
  const [showSort,        setShowSort]        = useState(false);

  const showWarning = getWarningStatus("evaluation") === "warning";
  const showBlocked = getWarningStatus("evaluation") === "exceeded";

  /* ── data ──────────────────────────────────────────────── */
  useEffect(() => {
    if (!user || !userData) return;
    if (!userData.tenantId) { setError(t("tenant_not_found")); setLoading(false); return; }
    loadCampaigns();
  }, [user, userData]);

  const loadCampaigns = async () => {
    if (!userData?.tenantId) return;
    try {
      setLoading(true); setError("");
      try {
        const fn = httpsCallable(functions, "getCampaigns");
        const r  = await fn();
        setCampaigns(r.data.campaigns || []);
        return;
      } catch {
        const q = query(
          collection(db, "campaigns"),
          where("tenantId", "==", userData.tenantId),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        setCampaigns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (err) { setError(err.message || t("error")); setCampaigns([]); }
    finally      { setLoading(false); }
  };

  const handleDeleteCampaign = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`${t("delete_campaign_confirm")} "${name}"? ${t("delete_campaign_warning")}`)) return;
    try {
      await deleteDoc(doc(db, "campaigns", id));
      setCampaigns(prev => prev.filter(c => c.id !== id));
    } catch (err) { alert(t("campaign_delete_failed") + err.message); }
  };

  /* ── filtered + sorted list ─────────────────────────────── */
  const filtered = useMemo(() => {
    let list = [...campaigns];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => c.name?.toLowerCase().includes(q));
    }
    if (channelFilter !== "all") {
      // For canonical channels (tiktok/snapchat/google/meta) the
      // filter is strict equality. The "other" pill is a catch-all:
      // it matches any channel value that isn't in the known set —
      // including user-supplied custom names like "LinkedIn" — and
      // also campaigns with no channel at all.
      list = list.filter(c => {
        const ch = c.channel;
        if (channelFilter === "other") {
          return !ch || !KNOWN_CHANNELS.has(ch) || ch === "other";
        }
        return ch === channelFilter;
      });
    }
    if (decisionFilter !== "all") {
      list = list.filter(c => {
        const d = c.latestDecision?.decision;
        // When the user filters by ADJUST, also match legacy PAUSE
        // saved before the backend normalize was added. The reverse
        // (filter PAUSE → match ADJUST) isn't needed because PAUSE is
        // no longer surfaced as a filter option.
        if (decisionFilter === "ADJUST") return d === "ADJUST" || d === "PAUSE";
        return d === decisionFilter;
      });
    }

    switch (sortBy) {
      case "oldest":        list.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)); break;
      case "most_evals":    list.sort((a, b) => (b.decisionsCount || 0) - (a.decisionsCount || 0)); break;
      case "last_decision": list.sort((a, b) => (b.latestDecision?.createdAt?.seconds || 0) - (a.latestDecision?.createdAt?.seconds || 0)); break;
      default:              list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)); break;
    }
    return list;
  }, [campaigns, search, channelFilter, decisionFilter, sortBy]);

  /* ── derived stats ──────────────────────────────────────── */
  const totalCampaigns    = campaigns.length;
  const stableCount       = campaigns.filter(c => c.latestDecision?.decision === "CONTINUE").length;
  // Match both the canonical "ADJUST" emitted by the current backend and
  // the legacy "PAUSE" value that may exist in older saved decisions.
  const opportunityCount  = campaigns.filter(c => {
    const d = c.latestDecision?.decision;
    return d === "ADJUST" || d === "PAUSE";
  }).length;
  const criticalCount     = campaigns.filter(c => c.latestDecision?.decision === "STOP").length;
  // Campaigns that have never been evaluated yet, OR were evaluated but
  // the saved decision value is something we don't recognize. Without
  // this bucket the four cards (Total / Stable / Opportunity / Critical)
  // don't add up — see `total = stable + opportunity + critical + pending`.
  const pendingCount      = totalCampaigns - stableCount - opportunityCount - criticalCount;

  const formatDate = (ts) => {
    try {
      const d = ts?.seconds ? new Date(ts.seconds * 1000)
        : ts?.toDate ? ts.toDate()
        : ts instanceof Date ? ts
        : typeof ts === "string" ? new Date(ts) : null;
      if (!d || isNaN(d.getTime())) return "—";
      return d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch { return "—"; }
  };

  /* ── guards ─────────────────────────────────────────────── */
  if (!canUseCampaigns()) return (
    <div className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white" : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"}`}>
      <Navigation />
      <div className="p-8 max-w-2xl mx-auto">
        <UpgradeCTA reason="feature_disabled" feature={t("campaign_decision")} planName="Free" />
      </div>
    </div>
  );

  if (loading) return (
    <div className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white" : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"}`}>
      <Navigation />
      <div className="p-8 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className={`h-20 rounded-2xl ${darkMode ? "bg-gray-800" : "bg-gray-200"}`} />
          <div className="grid grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className={`h-24 rounded-xl ${darkMode ? "bg-gray-800" : "bg-gray-200"}`} />)}
          </div>
          <div className={`h-14 rounded-xl ${darkMode ? "bg-gray-800" : "bg-gray-200"}`} />
          <div className="grid grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => <div key={i} className={`h-48 rounded-xl ${darkMode ? "bg-gray-800" : "bg-gray-200"}`} />)}
          </div>
        </div>
      </div>
    </div>
  );

  /* ── render ─────────────────────────────────────────────── */
  return (
    <div  className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white" : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"}`}>
      {showBlocked && (
        <UpgradeCTA reason="quota_exceeded" feature={t("evaluations")} current={usage?.evaluations?.current} quota={usage?.evaluations?.quota} />
      )}
      <Navigation />

      <div className="p-4 md:p-8 max-w-7xl mx-auto">

        {/* ── Page header ──────────────────────────────── */}
        <div className={`flex items-start justify-between mb-8 `}>
          <div className={""}>
            <h1 className={`text-3xl font-bold ${darkMode ? "text-white" : "text-gray-900"} mb-1`}>{t("campaign_decision")}</h1>
            <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-sm`}>{t("campaigns_subtitle")}</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className={`flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl font-semibold transition-colors flex-shrink-0 `}
          >
            <FiPlus />{t("new_campaign")}
          </button>
        </div>

        {/* ── Usage stats widget ───────────────────────── */}
<div className="mb-6">
  <UsageStatsWidget compact />
</div>

        {/* ── Quota warning ─────────────────────────────── */}
        {showWarning && !showBlocked && (
          <div className="mb-6">
            <UpgradeCTA reason="usage_warning" feature={t("evaluations")} current={usage?.evaluations?.current} quota={usage?.evaluations?.quota} inline />
          </div>
        )}

        {/* ── Error ────────────────────────────────────── */}
        {error && (
          <div className={`bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl mb-6 flex items-center justify-between `}>
            <span>{error}</span>
            <button onClick={loadCampaigns} className="underline text-sm">{t("try_again")}</button>
          </div>
        )}

        {/* ── Stats row ─────────────────────────────────── */}
        {/* 5-column grid so the breakdown adds up to Total:
            Total = Stable + Opportunity + Critical + Pending.
            On mobile we still show 2 columns; the 5th card simply wraps. */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {/* Total */}
          <div className={`${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200"} border rounded-xl p-4`}>
            <div className={`flex items-center gap-3 `}>
              <FiTarget className="text-blue-400 text-xl flex-shrink-0" />
              <div className={""}>
                <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs ${""}`}>{t("total_campaigns")}</p>
                <p className={`${darkMode ? "text-white" : "text-gray-900"} text-2xl font-bold`}>{totalCampaigns}</p>
              </div>
            </div>
          </div>

          {/* Stable (CONTINUE) */}
          <div className={`${darkMode ? "bg-gray-800/50" : "bg-white"} border border-green-500/30 rounded-xl p-4`}>
            <div className={`flex items-center gap-3 `}>
              <FiCheckCircle className="text-green-400 text-xl flex-shrink-0" />
              <div className={""}>
                <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs ${""}`}>{t("status_stable")}</p>
                <p className="text-green-400 text-2xl font-bold">{stableCount}</p>
              </div>
            </div>
          </div>

          {/* Opportunity (ADJUST — was previously labelled PAUSE) */}
          <div className={`${darkMode ? "bg-gray-800/50" : "bg-white"} border border-yellow-500/30 rounded-xl p-4`}>
            <div className={`flex items-center gap-3 `}>
              <FiPauseCircle className="text-yellow-400 text-xl flex-shrink-0" />
              <div className={""}>
                <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs ${""}`}>{t("status_opportunity")}</p>
                <p className="text-yellow-400 text-2xl font-bold">{opportunityCount}</p>
              </div>
            </div>
          </div>

          {/* Critical (STOP) */}
          <div className={`${darkMode ? "bg-gray-800/50" : "bg-white"} border border-red-500/30 rounded-xl p-4`}>
            <div className={`flex items-center gap-3 `}>
              <FiXCircle className="text-red-400 text-xl flex-shrink-0" />
              <div className={""}>
                <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs ${""}`}>{t("status_critical")}</p>
                <p className="text-red-400 text-2xl font-bold">{criticalCount}</p>
              </div>
            </div>
          </div>

          {/* Pending (no evaluation yet OR unknown decision value) */}
          <div className={`${darkMode ? "bg-gray-800/50" : "bg-white"} border border-gray-500/30 rounded-xl p-4`}>
            <div className={`flex items-center gap-3 `}>
              <FiHelpCircle className={`${darkMode ? "text-gray-300" : "text-gray-500"} text-xl flex-shrink-0`} />
              <div className={""}>
                <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs ${""}`}>{t("status_pending")}</p>
                <p className={`${darkMode ? "text-gray-200" : "text-gray-700"} text-2xl font-bold`}>{pendingCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            FILTER / SEARCH BAR
        ════════════════════════════════════════════ */}
        <div className={`${darkMode ? "bg-gray-800/40 border-gray-700" : "bg-white border-gray-200"} border rounded-2xl p-4 mb-6 space-y-3`}>

          {/* Row 1: search + view toggle + sort */}
          <div className={`flex gap-3 items-center `}>
            {/* Search */}
            <div className="relative flex-1">
              <FiSearch className={`absolute left-3 top-1/2 -translate-y-1/2 text-gray-400`} />
              <input
                type="text"
                placeholder={t("search_campaigns_placeholder")}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={`w-full ${darkMode ? "bg-gray-700/50 border-gray-600 text-white placeholder-gray-500" : "bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-400"} border rounded-xl py-2.5 text-sm focus:outline-none focus:border-blue-500 ${"pl-9 pr-4"}`}
              />
            </div>

            {/* View toggle */}
            <div className={`flex ${darkMode ? "bg-gray-700/60" : "bg-gray-200"} rounded-xl p-1 gap-1`}>
              <button onClick={() => setViewMode("grid")} className={`p-2 rounded-lg transition-colors ${viewMode === "grid" ? "bg-blue-600 text-white" : (darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900")}`}><FiGrid /></button>
              <button onClick={() => setViewMode("list")} className={`p-2 rounded-lg transition-colors ${viewMode === "list" ? "bg-blue-600 text-white" : (darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900")}`}><FiList /></button>
            </div>

            {/* Sort dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSort(!showSort)}
                className={`flex items-center gap-2 ${darkMode ? "bg-gray-700/60 hover:bg-gray-700 border-gray-600 text-gray-300" : "bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-600"} border px-3 py-2.5 rounded-xl text-sm transition-colors `}
              >
                <FiFilter className="text-xs" />
                <span className="hidden sm:inline">{t(`sort_${sortBy}`)}</span>
                <FiChevronDown className={`text-xs transition-transform ${showSort ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {showSort && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className={`absolute right-0 top-full mt-2 ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-xl shadow-2xl z-20 min-w-44 overflow-hidden`}
                  >
                    {SORT_OPTIONS.map(opt => (
                      <button
                        key={opt}
                        onClick={() => { setSortBy(opt); setShowSort(false); }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${sortBy === opt ? "bg-blue-600 text-white" : (darkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-100")} ${""}`}
                      >
                        {t(`sort_${opt}`)}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Row 2: Channel pills */}
          <div className={`flex flex-wrap gap-2 `}>
            <span className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs self-center px-1`}>{t("channel")}:</span>
            {CHANNELS.map(ch => (
              <button
                key={ch}
                onClick={() => setChannelFilter(ch)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                  channelFilter === ch
                    ? ch === "all"
                      ? "bg-blue-600 text-white border-blue-600"
                      : channelColor(ch)
                    : "border-gray-600 text-gray-400 hover:border-gray-500"
                }`}
              >
                {channelLabel(ch, lang, t)}
              </button>
            ))}
          </div>

          {/* Row 3: Decision pills */}
          <div className={`flex flex-wrap gap-2 `}>
            <span className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs self-center px-1`}>{t("latest_decision")}:</span>
            {DECISIONS.map(d => {
              const style = DECISION_STYLES[d];
              return (
                <button
                  key={d}
                  onClick={() => setDecisionFilter(d)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all  ${
                    decisionFilter === d
                      ? d === "all" ? "bg-blue-600 text-white border-blue-600" : style.cls
                      : "border-gray-600 text-gray-400 hover:border-gray-500"
                  }`}
                >
                  {style && <span className="text-sm">{style.icon}</span>}
                  {d === "all" ? t("all_label") : t(`decision_${d.toLowerCase()}`)}
                </button>
              );
            })}
          </div>

          {/* Active filter summary */}
          {(search || channelFilter !== "all" || decisionFilter !== "all") && (
            <div className={`flex items-center justify-between pt-1 `}>
              <span className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs`}>
                {filtered.length} {t("of_label")} {campaigns.length} {t("campaigns_label")}
              </span>
              <button
                onClick={() => { setSearch(""); setChannelFilter("all"); setDecisionFilter("all"); }}
                className="text-blue-400 hover:text-blue-300 text-xs underline"
              >
                {t("clear_filters")}
              </button>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════
            CAMPAIGN GRID / LIST
        ════════════════════════════════════════════ */}
        {filtered.length === 0 && !error ? (
          campaigns.length === 0 ? (
            /* Empty state — no campaigns at all */
            <div className={`${darkMode ? "bg-gray-800/30 border-gray-700" : "bg-white border-gray-200"} border rounded-2xl p-12 text-center`}>
              <div className="bg-blue-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiTarget className="text-blue-400 text-3xl" />
              </div>
              <h3 className={`${darkMode ? "text-white" : "text-gray-900"} text-xl font-semibold mb-2`}>{t("no_campaigns_yet")}</h3>
              <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} mb-6 max-w-sm mx-auto`}>{t("no_campaigns_desc")}</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
              >
                {t("create_first_campaign")}
              </button>
            </div>
          ) : (
            /* No results matching filters */
            <div className="text-center py-16">
              <FiSearch className={`${darkMode ? "text-gray-600" : "text-gray-400"} text-5xl mx-auto mb-3`} />
              <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} mb-2`}>{t("no_campaigns_match")}</p>
              <button
                onClick={() => { setSearch(""); setChannelFilter("all"); setDecisionFilter("all"); }}
                className="text-blue-400 hover:text-blue-300 text-sm underline"
              >
                {t("clear_filters")}
              </button>
            </div>
          )
        ) : viewMode === "grid" ? (
          <AnimatePresence mode="wait">
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {filtered.map((campaign, idx) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  idx={idx}
                  isRTL={isRTL}
                  t={t}
                  formatDate={formatDate}
                  onNavigate={() => navigate(`/campaigns/${campaign.id}`)}
                  onDelete={(e) => handleDeleteCampaign(e, campaign.id, campaign.name)}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              {/* List header */}
              <div className={`grid grid-cols-12 text-xs ${darkMode ? "text-gray-500" : "text-gray-400"} uppercase tracking-wide px-4 py-2 ${isRTL ? "flex-row-reverse text-right" : ""}`}>
                <div className="col-span-4">{t("campaign_name")}</div>
                <div className="col-span-2 text-center">{t("channel")}</div>
                <div className="col-span-2 text-center">{t("evaluations")}</div>
                <div className="col-span-2 text-center">{t("latest_decision")}</div>
                <div className="col-span-1 text-center">{t("col_status")}</div>
                <div className="col-span-1 text-center">{t("col_actions")}</div>
              </div>
              {filtered.map((campaign, idx) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  idx={idx}
                  isRTL={isRTL}
                  t={t}
                  formatDate={formatDate}
                  onNavigate={() => navigate(`/campaigns/${campaign.id}`)}
                  onDelete={(e) => handleDeleteCampaign(e, campaign.id, campaign.name)}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <CreateCampaignModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); loadCampaigns(); }}
        />
      )}
    </div>
  );
};

/* ── Campaign Card ────────────────────────────────────────── */
const CampaignCard = ({ campaign, idx, isRTL, t, formatDate, onNavigate, onDelete }) => {
  const { darkMode } = useDarkMode();
  const { isRTL: _rtl, lang } = useLanguage();
  const rtl = isRTL || _rtl;
  const decStyle = DECISION_STYLES[campaign.latestDecision?.decision];
  const chColor  = channelColor(campaign.channel || "other");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.04 }}
      dir={rtl ? "rtl" : "ltr"}
      className={`${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200"} border rounded-xl hover:border-blue-500/50 transition-all duration-200 group relative overflow-hidden cursor-pointer`}
      onClick={onNavigate}
    >
      {/* Channel accent top bar */}
      <div className={`h-1 w-full ${
        campaign.channel === "tiktok"   ? "bg-pink-500" :
        campaign.channel === "snapchat" ? "bg-yellow-500" :
        campaign.channel === "google"   ? "bg-blue-500" :
        campaign.channel === "meta"     ? "bg-indigo-500" : "bg-gray-600"
      }`} />

      <div className="p-5">
        {/* Header row: name + delete button */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            {campaign.channel && (
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold border mb-2 ${chColor}`}>
                {channelLabel(campaign.channel, lang, t)}
              </span>
            )}
            <h3 className={`${darkMode ? "text-white" : "text-gray-900"} font-semibold text-base leading-tight truncate`}>
              {campaign.name}
            </h3>
          </div>
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg flex-shrink-0"
            title={t("delete")}
          >
            <FiTrash2 className="text-xs" />
          </button>
        </div>

        {/* Status row — dot + text grouped together, naturally flips with dir */}
        <div className="flex items-center gap-2 mb-4 w-fit">
          <div className={`w-1.5 h-1.5 rounded-full ${campaign.status === "active" ? "bg-green-400 animate-pulse" : "bg-gray-500"}`} />
          <span className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs capitalize`}>
            {campaign.status === "active" ? t("status_active") : campaign.status || t("status_active")}
          </span>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className={`${darkMode ? "bg-gray-700/30" : "bg-gray-100"} rounded-lg p-3`}>
            <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mb-0.5`}>{t("evaluations")}</p>
            <p className={`${darkMode ? "text-white" : "text-gray-900"} font-bold text-xl`}>{campaign.decisionsCount || 0}</p>
          </div>
          <div className={`${darkMode ? "bg-gray-700/30" : "bg-gray-100"} rounded-lg p-3`}>
            <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mb-0.5`}>{t("latest_decision")}</p>
            {decStyle ? (
              <div className={`flex items-center gap-1.5 mt-0.5 font-semibold text-sm ${decStyle.cls.split(" ")[0]}`}>
                {decStyle.icon}
                <span>{t(`decision_${normalizeDecision(campaign.latestDecision.decision).toLowerCase()}`)}</span>
              </div>
            ) : (
              <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-sm mt-0.5`}>—</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className={`${darkMode ? "text-gray-600" : "text-gray-400"} text-xs`}>
          {t("created")}: {formatDate(campaign.createdAt)}
        </p>
      </div>
    </motion.div>
  );
};

/* ── Campaign Row (list view) ─────────────────────────────── */
const CampaignRow = ({ campaign, idx, isRTL, t, formatDate, onNavigate, onDelete }) => {
  const { darkMode } = useDarkMode();
  const { isRTL: _rtl, lang } = useLanguage();
  const rtl = isRTL || _rtl;
  const decStyle = DECISION_STYLES[campaign.latestDecision?.decision];
  const chColor  = channelColor(campaign.channel || "other");

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: idx * 0.03 }}
      onClick={onNavigate}
      className={`grid grid-cols-12 items-center ${darkMode ? "bg-gray-800/40 border-gray-700 hover:bg-gray-800/60" : "bg-white border-gray-200 hover:bg-gray-50"} border rounded-xl px-4 py-4 hover:border-blue-500/50 transition-all cursor-pointer group`}
    >
      <div className={`col-span-4 font-medium ${darkMode ? "text-white" : "text-gray-900"} truncate ${rtl ? "text-right" : ""}`}>{campaign.name}</div>
      <div className="col-span-2 text-center">
        {campaign.channel ? (
          <span className={`text-xs px-2 py-0.5 rounded-full border ${chColor}`}>
            {channelLabel(campaign.channel, lang, t)}
          </span>
        ) : <span className={`${darkMode ? "text-gray-600" : "text-gray-400"}`}>—</span>}
      </div>
      <div className={`col-span-2 text-center ${darkMode ? "text-white" : "text-gray-900"} font-semibold`}>{campaign.decisionsCount || 0}</div>
      <div className="col-span-2 text-center">
        {decStyle ? (
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${decStyle.cls}`}>
            {decStyle.icon} {t(`decision_${normalizeDecision(campaign.latestDecision.decision).toLowerCase()}`)}
          </span>
        ) : <span className={`${darkMode ? "text-gray-600" : "text-gray-400"} text-xs`}>—</span>}
      </div>
      <div className="col-span-1 text-center">
        <div className={`w-2 h-2 rounded-full mx-auto ${campaign.status === "active" ? "bg-green-400" : "bg-gray-500"}`} />
      </div>
      <div className="col-span-1 text-center">
        <button
          onClick={onDelete}
          className="p-1.5 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/10 rounded-lg transition-all"
        >
          <FiTrash2 className="text-xs" />
        </button>
      </div>
    </motion.div>
  );
};

/* ── Create Campaign Modal ─────────────────────────────────── */
const CreateCampaignModal = ({ onClose, onSuccess }) => {
  const { darkMode } = useDarkMode();
  const { t, isRTL, lang } = useLanguage();
  const [name,    setName]    = useState("");
  const [channel, setChannel] = useState("");
  // When channel === "other" the user gets an extra input to specify
  // what the channel actually is. The typed name is what we store as
  // campaign.channel — there's no separate sidecar field — so the
  // value round-trips and renders back verbatim everywhere
  // (channelLabel falls through to the raw string for non-known ids).
  const [customChannel, setCustomChannel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError(t("campaign_name")); return; }

    // Resolve the channel value to send. If the user picked Other,
    // require a custom name and use that as the channel string.
    let channelToSend = channel || undefined;
    if (channel === "other") {
      const custom = customChannel.trim();
      if (!custom) { setError(t("specify_channel")); return; }
      channelToSend = custom;
    }

    setLoading(true);
    try {
      const fn = httpsCallable(functions, "createCampaign");
      await fn({ name: name.trim(), channel: channelToSend });
      onSuccess();
    } catch (err) { setError(err.message || t("error")); }
    finally { setLoading(false); }
  };

  const inputCls = `w-full ${darkMode ? "bg-gray-700/50 border-gray-600 text-white placeholder-gray-500" : "bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-400"} border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500`;

  return (
    <div className={`fixed inset-0 ${darkMode ? "bg-black/70" : "bg-black/40"} backdrop-blur-sm flex items-center justify-center p-4 z-50`}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        
        className={`${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-2xl p-8 w-full max-w-md shadow-2xl`}
      >
        <h2 className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"} mb-1 ${""}`}>{t("create_campaign")}</h2>
        <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-sm mb-6 ${""}`}>{t("create_campaign_subtitle")}</p>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl mb-4 text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block ${darkMode ? "text-gray-300" : "text-gray-700"} text-sm font-medium mb-2`}>{t("campaign_name")} *</label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              className={inputCls} placeholder={t("campaign_name_placeholder")} required autoFocus
            />
          </div>
          <div>
            <label className={`block ${darkMode ? "text-gray-300" : "text-gray-700"} text-sm font-medium mb-2`}>{t("channel")}</label>
            <select value={channel} onChange={e => { setChannel(e.target.value); setCustomChannel(""); }} className={inputCls}>
              <option value="">{t("select_channel")}</option>
              {["tiktok","snapchat","google","meta","other"].map(c => (
                <option key={c} value={c}>
                  {/* Use the same channelLabel helper as the rest of
                      the page so the Arabic translation of "Other"
                      appears here too ("أخرى"), while keeping
                      "Tiktok"/"Snapchat" as proper-noun brand names. */}
                  {channelLabel(c, lang, t)}
                </option>
              ))}
            </select>
          </div>
          {/* When "Other" is selected, ask the user to name the
              channel. The typed value is what we send as the channel
              string — no separate field — so it round-trips and the
              filter pills + cards display it verbatim afterwards. */}
          {channel === "other" && (
            <div>
              <label className={`block ${darkMode ? "text-gray-300" : "text-gray-700"} text-sm font-medium mb-2`}>{t("specify_channel")} *</label>
              <input
                type="text"
                value={customChannel}
                onChange={e => setCustomChannel(e.target.value)}
                className={inputCls}
                placeholder={t("specify_channel_placeholder")}
                maxLength={40}
                autoFocus
                required
              />
            </div>
          )}
          <div className={`flex gap-3 pt-2 `}>
            <button type="button" onClick={onClose} disabled={loading}
              className={`flex-1 ${darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} text-sm py-3 rounded-xl transition-colors ${darkMode ? "text-white" : "text-gray-800"}`}>
              {t("cancel")}
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-3 rounded-xl font-semibold text-sm transition-colors">
              {loading ? t("loading") : t("create_campaign")}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CampaignsPage;